import { Loan, PaymentMethodType, Pricing } from '@nemuru-eng/js-api-client';

export interface VehicleAmountRange {
  min: number;
  max: number;
}

const getAutoLoanPricings = (pricings: Pricing[]): Pricing[] =>
  pricings.filter((pricing) => pricing.paymentMethodTypes.includes(PaymentMethodType.AUTO_LOAN));

const getPricingsMatchingLoanConditions = (pricings: Pricing[], loan: Loan | undefined): Pricing[] => {
  if (!loan) {
    return [];
  }

  const principal = loan.loanConditions.principal.amount;
  const term = loan.loanConditions.loanCalculator.term.period;

  return pricings.filter((pricing) => {
    const matchesPrincipal = principal >= pricing.rangePrincipalMin && principal <= pricing.rangePrincipalMax;
    const matchesTerm =
      term === null ||
      term === undefined ||
      ((pricing.rangePeriodMin === null || term >= pricing.rangePeriodMin) &&
        (pricing.rangePeriodMax === null || term <= pricing.rangePeriodMax));

    return matchesPrincipal && matchesTerm;
  });
};

export const resolvePrincipalAmountRangeFromPricings = ({
  pricings,
  loan,
  fallbackPrincipal,
}: {
  pricings: Pricing[] | null | undefined;
  loan: Loan | undefined;
  fallbackPrincipal: number | null;
}): VehicleAmountRange => {
  const fallback = fallbackPrincipal ?? 0;

  if (!pricings?.length) {
    return { min: fallback, max: fallback };
  }

  const autoLoanPricings = getAutoLoanPricings(pricings);
  const candidatePricings = autoLoanPricings.length > 0 ? autoLoanPricings : pricings;
  const matchingPricings = getPricingsMatchingLoanConditions(candidatePricings, loan);
  const relevantPricings = matchingPricings.length > 0 ? matchingPricings : candidatePricings;

  if (relevantPricings.length === 0) {
    return { min: fallback, max: fallback };
  }

  return {
    min: Math.min(...relevantPricings.map((pricing) => pricing.rangePrincipalMin)),
    max: Math.max(...relevantPricings.map((pricing) => pricing.rangePrincipalMax)),
  };
};

export const resolveVehicleAmountRange = ({
  pricings,
  loan,
  downPaymentAmount,
  discountAmount,
  fallbackPrincipal,
}: {
  pricings: Pricing[] | null | undefined;
  loan: Loan | undefined;
  downPaymentAmount: number | null;
  discountAmount: number | null;
  fallbackPrincipal: number | null;
}): VehicleAmountRange => {
  const downPayment = downPaymentAmount ?? 0;
  const discount = discountAmount ?? 0;

  const principalRange = resolvePrincipalAmountRangeFromPricings({
    pricings,
    loan,
    fallbackPrincipal,
  });

  return {
    min: principalRange.min + downPayment + discount,
    max: principalRange.max + downPayment + discount,
  };
};

export const isVehicleAmountWithinRange = (amount: number, range: VehicleAmountRange): boolean => amount >= range.min;
